import React, { useState, useEffect, useCallback } from "react";
import { useUserContext } from "../../utils/UserContext";
import { tincPermis } from "../../utils/Permisos";
import TeclaEscEnter from "../../utils/Teclaescenter";
import { API_URL } from "../../utils/config";
import { formatDate, formatNumero } from "../../utils/Utilitats";
import AjudaCataleg from "./AjudaCataleg";
import { BotoAjuda } from "./AjudaModal";

function idMaterial(m) {
  return m ? Number(m.idMaterials) : null;
}

function idTipusDe(item) {
  return item ? Number(item.idTipusDeMaterials ?? item.TipusDeMaterials_idTipusDeMaterials) : null;
}

function idCategoriaDe(item) {
  return item ? Number(item.idCategories ?? item.Categories_idCategories) : null;
}

function esActiu(v) {
  return v === true || v === 1 || v === "1";
}

function dataISO(v) {
  if (!v) return "";
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
}

function Detallsmaterial({
  mode,
  material,
  materialsLlista,
  onNavigate,
  onCancel,
  onSave,
  onCanviarMode,
  onDeleted,
}) {
  const { empresaSeleccionada, rolUsuari } = useUserContext();
  TeclaEscEnter(onCancel, null);

  const isReadOnly = mode === "consultar" || mode === "esborrar";
  const avui = new Date().toISOString().split("T")[0];

  const [selectedCategory, setSelectedCategory] = useState(null);
  const [idTipusDeMaterials, setIdTipusDeMaterials] = useState(null);
  const [nomGeneric, setNomGeneric] = useState("");
  const [descripcio, setDescripcio] = useState("");
  const [unitatMesura, setUnitatMesura] = useState("ut");
  const [actiu, setActiu] = useState(true);
  const [dataAlta, setDataAlta] = useState(avui);
  const [dataBaixa, setDataBaixa] = useState("");
  const [tipusOptions, setTipusOptions] = useState([]);
  const [unitatOptions, setUnitatOptions] = useState(["ml", "ut", "m2", "m3", "l", "Pa"]);
  const [menuAccions, setMenuAccions] = useState(false);
  const [mostraAjuda, setMostraAjuda] = useState(false);

  const resetForm = useCallback(() => {
    setSelectedCategory(null);
    setIdTipusDeMaterials(null);
    setNomGeneric("");
    setDescripcio("");
    setUnitatMesura("ut");
    setActiu(true);
    setDataAlta(avui);
    setDataBaixa("");
  }, [avui]);

  const llista = Array.isArray(materialsLlista) ? materialsLlista : [];
  const indexActual = material ? llista.findIndex((m) => idMaterial(m) === idMaterial(material)) : -1;
  const potNavegar = ["consultar", "modificar"].includes(mode) && llista.length > 1 && indexActual >= 0;

  const anarAnterior = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === 0 ? llista.length - 1 : indexActual - 1;
    onNavigate?.(llista[nouIndex]);
  };

  const anarSeguent = () => {
    if (!potNavegar) return;
    const nouIndex = indexActual === llista.length - 1 ? 0 : indexActual + 1;
    onNavigate?.(llista[nouIndex]);
  };

  useEffect(() => {
    if (material && (mode === "consultar" || mode === "modificar" || mode === "esborrar")) {
      setIdTipusDeMaterials(idTipusDe(material) || null);
      setSelectedCategory(idCategoriaDe(material) || null);
      setNomGeneric(material.NomGeneric || "");
      setDescripcio(material.Descripcio || "");
      setUnitatMesura(material.UnitatMesura || "ut");
      setActiu(esActiu(material.Actiu));
      setDataAlta(dataISO(material.DataAlta) || avui);
      setDataBaixa(dataISO(material.DataBaixa));
    } else if (mode === "nou") {
      resetForm();
    }
    setMenuAccions(false);
  }, [material, mode, resetForm, avui]);

  useEffect(() => {
    const fetchTipusMaterials = async () => {
      try {
        const response = await fetch(`${API_URL}/Tipusdematerials.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            idEmpresa: empresaSeleccionada?.IdEmpresa,
            mostrarActius: 1,
          }),
        });
        const data = await response.json();
        setTipusOptions(data.success ? data.tipusDeMaterials || [] : []);
      } catch (error) {
        console.error("Error al fetchTipusMaterials:", error);
        setTipusOptions([]);
      }
    };
    fetchTipusMaterials();
  }, [empresaSeleccionada]);

  useEffect(() => {
    const fetchUnitatsEnum = async () => {
      try {
        const resp = await fetch(`${API_URL}/Unitatsmesura.php`, { method: "POST" });
        const data = await resp.json();
        if (data.success && Array.isArray(data.enumValues) && data.enumValues.length) {
          setUnitatOptions(data.enumValues);
        }
      } catch (error) {
        console.error("Error al fetchUnitatsEnum:", error);
      }
    };
    fetchUnitatsEnum();
  }, []);

  useEffect(() => {
    if (idTipusDeMaterials && tipusOptions.length > 0 && !selectedCategory) {
      const found = tipusOptions.find((t) => idTipusDe(t) === Number(idTipusDeMaterials));
      const catId = idCategoriaDe(found);
      if (catId) setSelectedCategory(catId);
    }
  }, [idTipusDeMaterials, tipusOptions, selectedCategory]);

  useEffect(() => {
    if (mode === "modificar" && dataBaixa) {
      setActiu(false);
    }
  }, [dataBaixa, mode]);

  const tipusFiltrats = (tipusOptions || []).filter(
    (t) => !selectedCategory || idCategoriaDe(t) === Number(selectedCategory)
  );
  const tipusActualAlLlistat = tipusFiltrats.some((t) => idTipusDe(t) === Number(idTipusDeMaterials));
  const tipusPerSelect =
    idTipusDeMaterials && !tipusActualAlLlistat
      ? [
          {
            idTipusDeMaterials,
            Tipus: material?.Tipus || `Tipus #${idTipusDeMaterials}`,
            Categories_idCategories: selectedCategory,
          },
          ...tipusFiltrats,
        ]
      : tipusFiltrats;

  const categoriesUniques = Array.from(
    new Map(
      (tipusOptions || [])
        .filter((t) => idCategoriaDe(t) && t.Categoria)
        .map((t) => [idCategoriaDe(t), { id: idCategoriaDe(t), nom: t.Categoria }])
    ).values()
  ).sort((a, b) => a.nom.localeCompare(b.nom, "ca"));
  if (selectedCategory && !categoriesUniques.some((c) => Number(c.id) === Number(selectedCategory))) {
    categoriesUniques.unshift({
      id: Number(selectedCategory),
      nom: material?.Categoria || `Categoria #${selectedCategory}`,
    });
  }

  const categoriaNom =
    categoriesUniques.find((c) => Number(c.id) === Number(selectedCategory))?.nom ||
    material?.Categoria ||
    "\u2014";
  const tipusNom =
    tipusOptions.find((t) => idTipusDe(t) === Number(idTipusDeMaterials))?.Tipus ||
    material?.Tipus ||
    "\u2014";

  const titol =
    mode === "nou"
      ? "Nou material"
      : mode === "esborrar"
      ? "Esborrar material"
      : mode === "modificar"
      ? "Modificar material"
      : "Materials · Fitxa";

  const subtitol =
    mode === "nou"
      ? "Tria la categoria i el tipus, i omple el nom genèric."
      : mode === "esborrar"
      ? "No es podrà esborrar si té productes associats."
      : mode === "modificar"
      ? "Edita el material i la seva classificació."
      : "Consulta la informació del material.";

  const validarFormulari = () => {
    if (!idTipusDeMaterials) {
      alert("El camp Tipus de material és obligatori.");
      return false;
    }
    if (!nomGeneric.trim()) {
      alert("El camp Nom genèric no pot estar buit.");
      return false;
    }
    if (nomGeneric.trim().length > 45) {
      alert("El nom genèric no pot superar 45 caràcters.");
      return false;
    }
    if (descripcio.trim().length > 100) {
      alert("La descripció no pot superar 100 caràcters.");
      return false;
    }
    if (!dataAlta) {
      alert("El camp Data d alta és obligatori.");
      return false;
    }
    if (dataBaixa && actiu) {
      alert("Si hi ha data de baixa, Actiu ha de ser no.");
      return false;
    }
    return true;
  };

  const payloadActualitzat = () => ({
    ...material,
    idMaterials: idMaterial(material),
    TipusDeMaterials_idTipusDeMaterials: idTipusDeMaterials,
    idTipusDeMaterials,
    Categories_idCategories: selectedCategory,
    idCategories: selectedCategory,
    Categoria: categoriaNom === "\u2014" ? "" : categoriaNom,
    Tipus: tipusNom === "\u2014" ? "" : tipusNom,
    NomGeneric: nomGeneric.trim(),
    Descripcio: descripcio.trim(),
    UnitatMesura: unitatMesura,
    Actiu: actiu ? 1 : 0,
    DataAlta: dataAlta,
    DataBaixa: dataBaixa || null,
  });

  const handleSubmit = async () => {
    if (mode !== "esborrar" && !validarFormulari()) return;

    const materialData = {
      idMaterials: material?.idMaterials || null,
      TipusDeMaterials_idTipusDeMaterials: idTipusDeMaterials,
      NomGeneric: nomGeneric.trim(),
      Descripcio: descripcio.trim(),
      UnitatMesura: unitatMesura,
      Actiu: actiu ? 1 : 0,
      DataAlta: dataAlta,
      DataBaixa: dataBaixa || null,
      Empresa_idEmpresa: empresaSeleccionada?.IdEmpresa || null,
    };

    try {
      if (mode === "nou") {
        const resp = await fetch(`${API_URL}/Noumaterial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(materialData),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.({
            ...payloadActualitzat(),
            idMaterials: data.idMaterial,
          });
        } else {
          alert("Error en crear el material: " + data.message);
        }
      } else if (mode === "modificar") {
        const resp = await fetch(`${API_URL}/Modificarmaterial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(materialData),
        });
        const data = await resp.json();
        if (data.success) {
          onSave?.(payloadActualitzat());
        } else {
          alert("Error en modificar el material: " + data.message);
        }
      } else if (mode === "esborrar") {
        const resp = await fetch(`${API_URL}/Esborrarmaterial.php`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idMaterials: idMaterial(material) }),
        });
        const data = await resp.json();
        if (data.success) {
          onDeleted?.();
        } else {
          alert("Error en esborrar el material: " + data.message);
        }
      }
    } catch (error) {
      console.error("Error en handleSubmit Material:", error);
      alert("S'ha produït un error en el procés.");
    }
  };

  const mostrarDataBaixa = mode === "modificar" || (mode !== "nou" && dataBaixa);

  const camp = (label, required, content, mida = "mig") => {
    const extra =
      mida === "ample"
        ? "dv2-field--ample"
        : mida === "llarg"
        ? "dv2-field--llarg"
        : mida === "curt"
        ? "dv2-field--curt"
        : "dv2-field--mig";
    return (
      <label className={`dv2-field ${extra}`}>
        <span className={`dv2-label ${required && !isReadOnly ? "dv2-required" : ""}`}>{label}</span>
        {content}
      </label>
    );
  };

  const valorBuit = (v) => (v ? v : "\u2014");

  return (
    <form className="dv2-entity" onSubmit={(e) => e.preventDefault()}>
      {mostraAjuda && <AjudaCataleg pestanya="materials" onTancar={() => setMostraAjuda(false)} />}
      <div className="dv2-pageHeader">
        <div className="dv2-pageHeader-main">
          <div className={`dv2-pageHeader-icon ${mode === "esborrar" ? "is-danger" : ""}`} aria-hidden="true">
            {mode === "esborrar" ? "🗑️" : "🧱"}
          </div>
          <div>
            <h1 className="dv2-pageHeader-title">{titol}</h1>
            <p className="dv2-pageHeader-subtitle">{subtitol}</p>
          </div>
        </div>
        <div className="dv2-pageHeader-actions">
          <BotoAjuda title="Com funcionen els Materials" onClick={() => setMostraAjuda(true)} />
          <button type="button" className="dv2-btn" onClick={onCancel}>
            Tornar
          </button>
          {mode === "consultar" && tincPermis(rolUsuari, "MODIFICAR") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={() => onCanviarMode?.("modificar")}>
              Modificar
            </button>
          )}
          {mode === "consultar" && tincPermis(rolUsuari, "ESBORRAR") && (
            <div style={{ position: "relative" }}>
              <button type="button" className="dv2-btn" onClick={() => setMenuAccions((v) => !v)}>
                Més accions
              </button>
              {menuAccions && (
                <div className="dv2-menu" style={{ right: 0, top: "42px" }}>
                  <button
                    type="button"
                    className="dv2-menu-item is-danger"
                    onClick={() => {
                      setMenuAccions(false);
                      onCanviarMode?.("esborrar");
                    }}
                  >
                    Esborrar
                  </button>
                </div>
              )}
            </div>
          )}
          {(mode === "nou" || mode === "modificar") && (
            <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
              Desar
            </button>
          )}
          {mode === "esborrar" && (
            <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
              Esborrar material
            </button>
          )}
        </div>
      </div>

      {mode === "esborrar" && (
        <div className="dv2-banner dv2-banner--danger">
          Aquesta acció no es pot desfer. Si el material té productes associats, el servidor no el deixarà esborrar.
        </div>
      )}

      <div className="dv2-entity-body">
        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">1</span>
            <h2 className="dv2-card-title">Classificació</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Categoria",
              false,
              isReadOnly ? (
                <span className="dv2-value">{categoriaNom}</span>
              ) : (
                <select
                  className="dv2-select"
                  value={selectedCategory != null ? String(selectedCategory) : ""}
                  onChange={(e) => {
                    setSelectedCategory(e.target.value ? Number(e.target.value) : null);
                    setIdTipusDeMaterials(null);
                  }}
                >
                  <option value="">Selecciona una categoria</option>
                  {categoriesUniques.map((cat) => (
                    <option key={cat.id} value={String(cat.id)}>
                      {cat.nom}
                    </option>
                  ))}
                </select>
              )
            )}
            {camp(
              "Tipus",
              true,
              isReadOnly ? (
                <span className="dv2-value">{tipusNom}</span>
              ) : (
                <select
                  className="dv2-select"
                  value={idTipusDeMaterials != null ? String(idTipusDeMaterials) : ""}
                  onChange={(e) => {
                    const id = e.target.value ? Number(e.target.value) : null;
                    setIdTipusDeMaterials(id);
                    if (id) {
                      const found = tipusOptions.find((t) => idTipusDe(t) === id);
                      const catId = idCategoriaDe(found);
                      if (catId) setSelectedCategory(catId);
                    }
                  }}
                >
                  <option value="">Selecciona un tipus</option>
                  {tipusPerSelect.map((tipus) => (
                    <option key={idTipusDe(tipus)} value={String(idTipusDe(tipus))}>
                      {tipus.Tipus}
                    </option>
                  ))}
                </select>
              ),
              "llarg"
            )}
          </div>
        </section>

        <section className="dv2-card">
          <div className="dv2-card-head">
            <span className="dv2-card-num">2</span>
            <h2 className="dv2-card-title">Dades del material</h2>
          </div>
          <div className="dv2-fields dv2-fields--sizes">
            {camp(
              "Nom genèric",
              true,
              isReadOnly ? (
                <span className="dv2-value">{valorBuit(nomGeneric)}</span>
              ) : (
                <input
                  type="text"
                  className="dv2-input"
                  value={nomGeneric}
                  maxLength={45}
                  autoFocus
                  onChange={(e) => setNomGeneric(e.target.value)}
                />
              ),
              "llarg"
            )}
            {camp(
              "Descripció",
              false,
              isReadOnly ? (
                <span className="dv2-value">{valorBuit(descripcio)}</span>
              ) : (
                <input
                  type="text"
                  className="dv2-input"
                  value={descripcio}
                  maxLength={100}
                  onChange={(e) => setDescripcio(e.target.value)}
                />
              ),
              "ample"
            )}
            {camp(
              "Unitat de mesura",
              false,
              isReadOnly ? (
                <span className="dv2-value">{valorBuit(unitatMesura)}</span>
              ) : (
                <select className="dv2-select" value={unitatMesura} onChange={(e) => setUnitatMesura(e.target.value)}>
                  {unitatOptions.map((um) => (
                    <option key={um} value={um}>
                      {um}
                    </option>
                  ))}
                </select>
              )
            )}
            {camp(
              "Estoc físic",
              false,
              <span className="dv2-value">{formatNumero(material?.EstocFisic || 0)}</span>,
              "curt"
            )}
            {camp(
              "Actiu",
              false,
              isReadOnly ? (
                <span className={`dv2-chip ${actiu ? "dv2-chip--ok" : ""}`}>{actiu ? "Sí" : "No"}</span>
              ) : (
                <span className="dv2-toggleRow">
                  <span className="dv2-toggle">
                    <input type="checkbox" checked={actiu} onChange={(e) => setActiu(e.target.checked)} />
                    <span className="dv2-toggle-track" />
                  </span>
                  {actiu ? "Sí" : "No"}
                </span>
              ),
              "curt"
            )}
            {camp(
              "Data d'alta",
              true,
              isReadOnly ? (
                <span className="dv2-value">{formatDate(dataAlta)}</span>
              ) : (
                <input
                  type="date"
                  className="dv2-input"
                  value={dataAlta}
                  onChange={(e) => setDataAlta(e.target.value)}
                />
              )
            )}
            {mostrarDataBaixa
              ? camp(
                  "Data de baixa",
                  false,
                  isReadOnly ? (
                    <span className="dv2-value">{dataBaixa ? formatDate(dataBaixa) : "\u2014"}</span>
                  ) : (
                    <input
                      type="date"
                      className="dv2-input"
                      value={dataBaixa}
                      onChange={(e) => setDataBaixa(e.target.value)}
                    />
                  )
                )
              : null}
          </div>
        </section>
      </div>

      {(potNavegar || mode === "nou" || mode === "modificar" || mode === "esborrar") && (
        <div className="dv2-entity-dock">
          {potNavegar && (
            <div className="dv2-recordNav">
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarAnterior} title="Anterior">
                {"\u2039"}
              </button>
              <span className="dv2-recordNav-info">
                {indexActual + 1} / {llista.length}
              </span>
              <button type="button" className="dv2-btn dv2-recordNav-btn" onClick={anarSeguent} title="Següent">
                {"\u203A"}
              </button>
            </div>
          )}
          {(mode === "nou" || mode === "modificar" || mode === "esborrar") && (
            <div className="dv2-footerActions">
              <button type="button" className="dv2-btn" onClick={onCancel}>
                Cancel·lar
              </button>
              {mode === "esborrar" ? (
                <button type="button" className="dv2-btn dv2-btn--danger" onClick={handleSubmit}>
                  Esborrar material
                </button>
              ) : (
                <button type="button" className="dv2-btn dv2-btn--primary" onClick={handleSubmit}>
                  {mode === "nou" ? "Desar material" : "Desar"}
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </form>
  );
}

export default Detallsmaterial;
